import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  ArrowUpDown,
  Ban,
  Calendar,
  CheckCircle2,
  Eye,
  FileSpreadsheet,
  FileText,
  Filter,
  Mail,
  Receipt,
  RefreshCw,
  RotateCcw,
  Search,
} from "lucide-react";
import React from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { OrderMetadata } from "../../../types/invoice";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/Dialog";
import { Input } from "../../components/ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { fetchWithAuth } from "../../hooks/useAuth";
import { Order } from "../../types/orders";
import { formatDate, formatPrice } from "../../utils/format";

type OrderWithMetadata = Order & {
  metadata?: OrderMetadata;
};

export function InvoicesPage() {
  const queryClient = useQueryClient();
  const [selectedInvoice, setSelectedInvoice] = React.useState<{
    series: string;
    number: string;
    orderId: number;
    action: "cancel" | "storno" | "email";
  } | null>(null);
  const [filters, setFilters] = React.useState({
    search: "",
    dateFrom: "",
    dateTo: "",
  });

  // Function to handle invoice download
  const handleInvoiceDownload = async (
    series: string,
    number: string,
    download = false
  ) => {
    try {
      const response = await fetchWithAuth(
        `/api/admin/invoices/${series}/${number}/download?download=${download}`,
        {
          headers: {
            Accept: "application/pdf",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.message || "Eroare la descărcarea facturii");
        throw new Error(errorData.message || "Eroare la descărcarea facturii");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      if (download) {
        // Force download
        const a = document.createElement("a");
        a.href = url;
        a.download = `factura-${series}-${number}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        // Open in new tab
        const newWindow = window.open(url, "_blank");
        if (!newWindow) {
          toast.error(
            "Popup-ul a fost blocat. Vă rugăm să permiteți popup-uri pentru acest site.",
            {
              duration: 5000,
              position: "top-center",
            }
          );
        } else {
          toast.success("Factura a fost deschisă într-o fereastră nouă", {
            duration: 3000,
            position: "top-center",
          });
        }
      }

      // Clean up
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Eroare la descărcarea facturii");
    }
  };

  // Invoice mutations
  const invoiceMutation = useMutation({
    mutationFn: async ({
      series,
      number,
      reason,
      action,
      email,
      subject,
      body,
    }: {
      series: string;
      number: string;
      reason?: string;
      action: "cancel" | "storno" | "email";
      email?: string;
      subject?: string;
      body?: string;
    }) => {
      const url =
        action === "email"
          ? `/api/admin/invoices/${series}/${number}/email`
          : `/api/admin/invoices/${series}/${number}/${action}`;

      const response = await fetchWithAuth(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(
          action === "email"
            ? { to: [email], subject, bodyText: body }
            : reason
            ? { reason }
            : {}
        ),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Eroare la procesarea facturii");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice-orders"] });
      queryClient.invalidateQueries({ queryKey: ["invoice-stats"] });
      setSelectedInvoice(null);
      toast.success(
        selectedInvoice?.action === "cancel"
          ? "Factură anulată cu succes"
          : selectedInvoice?.action === "storno"
          ? "Factură stornată cu succes"
          : "Email trimis cu succes"
      );
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(10);

  // Fetch orders with invoices
  const {
    data: ordersResponse,
    isLoading,
    error: ordersError,
    isFetching,
    refetch,
  } = useQuery<{
    orders: OrderWithMetadata[];
    pagination: {
      total: number;
      pages: number;
      currentPage: number;
      perPage: number;
    };
  }>({
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryKey: ["invoice-orders", filters, page, pageSize],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", pageSize.toString());
      if (filters.search) params.append("search", filters.search);
      if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.append("dateTo", filters.dateTo);

      const response = await fetchWithAuth(
        `/api/admin/invoices/orders?${params}`
      );
      if (!response.ok) throw new Error("Eroare la încărcarea facturilor");
      return response.json() as Promise<{
        orders: Order[];
        pagination: {
          total: number;
          pages: number;
          currentPage: number;
          perPage: number;
        };
      }>;
    },
  });

  // Fetch SmartBill service status
  const { data: smartbillStatus, error: statusError } = useQuery({
    queryKey: ["smartbill-status"],
    queryFn: async () => {
      const response = await fetchWithAuth("/api/admin/invoices/status");
      if (!response.ok)
        throw new Error("Eroare la verificarea statusului SmartBill");
      return response.json() as Promise<{
        status: "operational" | "degraded" | "down";
        message?: string;
        lastSync?: string;
      }>;
    },
  });

  // Fetch invoice statistics
  const { data: stats, error: statsError } = useQuery({
    queryKey: ["invoice-stats"],
    queryFn: async () => {
      const response = await fetchWithAuth("/api/admin/invoices/stats");
      if (!response.ok) throw new Error("Eroare la încărcarea statisticilor");
      return response.json() as Promise<{
        total: number;
        totalValue: number;
        thisMonth: number;
        thisMonthValue: number;
      }>;
    },
  });

  return (
    <div className="p-6 space-y-8 bg-background  min-h-screen">
      <Card className="flex justify-between items-center ">
        <div className="flex items-center gap-2">
          <Receipt className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Facturi</h1>
            {smartbillStatus && (
              <div className="flex items-center gap-2 mt-1">
                {smartbillStatus.status === "operational" ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : smartbillStatus.status === "degraded" ? (
                  <AlertTriangle className="w-4 h-4 text-yellow-600" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-600" />
                )}
                <p
                  className={`text-sm ${
                    smartbillStatus.status === "operational"
                      ? "text-green-600"
                      : smartbillStatus.status === "degraded"
                      ? "text-yellow-600"
                      : "text-red-600"
                  }`}
                >
                  SmartBill:{" "}
                  {smartbillStatus.status === "operational"
                    ? "Operațional"
                    : smartbillStatus.status === "degraded"
                    ? "Performanță Redusă"
                    : "Indisponibil"}
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4">
          {(ordersError || statusError || statsError) && (
            <div className="bg-red-50 text-red-700 px-4 py-2 rounded-md text-sm">
              Eroare la încărcarea datelor. Vă rugăm să reîncercați.
            </div>
          )}
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={async () => {
              const currentOrders = ordersResponse?.orders || [];
              const orderIds = currentOrders.map((order) => order.id);

              try {
                await fetchWithAuth("/api/admin/invoices/refresh", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ orderIds }),
                });

                // Refresh data after status update
                refetch();
                queryClient.invalidateQueries({ queryKey: ["invoice-stats"] });
                queryClient.invalidateQueries({
                  queryKey: ["smartbill-status"],
                });
                toast.success("Status actualizat cu succes");
              } catch (error) {
                console.error("Failed to refresh statuses:", error);
                toast.error("Eroare la actualizarea statusului");
              }
            }}
            disabled={isFetching}
          >
            <RefreshCw
              className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`}
            />
            {isFetching ? "Se actualizează..." : "Actualizare"}
          </Button>
          <Button
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
            onClick={async () => {
              try {
                const response = await fetchWithAuth(
                  "/api/admin/invoices/export",
                  {
                    method: "GET",
                    headers: {
                      Accept:
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    },
                  }
                );
                if (!response.ok) {
                  throw new Error("Eroare la exportul facturilor");
                }
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `facturi-${formatDate(new Date())}.xlsx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                toast.success("Export realizat cu succes");
              } catch (error) {
                console.error("Export error:", error);
                toast.error("Eroare la exportul facturilor");
              }
            }}
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel
          </Button>
        </div>
      </Card>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <FileText className="w-4 h-4" />
              Total Facturi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {stats?.total || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Calendar className="w-4 h-4" />
              Luna Curentă
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {stats?.thisMonth || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <ArrowUpDown className="w-4 h-4" />
              Valoare Luna Curentă
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {formatPrice(stats?.thisMonthValue || 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-muted-foreground" />
          <h2 className="font-semibold text-foreground">Filtre</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Caută după serie, număr, client..."
              value={filters.search}
              onChange={(e) =>
                setFilters((f) => ({ ...f, search: e.target.value }))
              }
            />
          </div>
          <div className="relative">
            <Calendar className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input
              className="pl-9"
              type="date"
              value={filters.dateFrom}
              onChange={(e) =>
                setFilters((f) => ({ ...f, dateFrom: e.target.value }))
              }
            />
          </div>
          <div className="relative">
            <Calendar className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input
              className="pl-9"
              type="date"
              value={filters.dateTo}
              onChange={(e) =>
                setFilters((f) => ({ ...f, dateTo: e.target.value }))
              }
            />
          </div>
        </div>
      </Card>

      {/* Invoices Table */}
      <Card className=" shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">Serie/Număr</TableHead>
                <TableHead className="font-semibold">Data</TableHead>
                <TableHead className="font-semibold">Client</TableHead>
                <TableHead className="font-semibold">Valoare</TableHead>
                <TableHead className="font-semibold">Comandă</TableHead>
                <TableHead className="font-semibold">Acțiuni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    <div className="flex items-center justify-center text-muted-foreground">
                      Se încarcă...
                    </div>
                  </TableCell>
                </TableRow>
              ) : ordersResponse?.orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    <div className="text-muted-foreground">
                      Nu există facturi
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                ordersResponse?.orders.map((order) => (
                  <TableRow key={order.id} className="hover:bg-muted">
                    <TableCell className="font-medium">
                      <Button
                        variant="link"
                        className="text-blue-600 hover:text-blue-800"
                        onClick={() =>
                          handleInvoiceDownload(
                            order.invoiceSeries!,
                            order.invoiceNumber!,
                            true
                          )
                        }
                      >
                        {order.invoiceSeries}/{order.invoiceNumber}
                      </Button>
                    </TableCell>
                    <TableCell>{formatDate(order.createdAt)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {order.metadata?.customerName}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {order.metadata?.customerEmail}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatPrice(order.totalPrice)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="link"
                        className="text-blue-600 hover:text-blue-800"
                        asChild
                      >
                        <Link to={`/orders/${order.id}`}>#{order.id}</Link>
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          {
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                                onClick={() =>
                                  handleInvoiceDownload(
                                    order.invoiceSeries!,
                                    order.invoiceNumber!,
                                    false
                                  )
                                }
                              >
                                <Eye className="w-4 h-4" />
                                Vizualizează
                              </Button>
                              {/* Show action buttons based on invoice status */}
                              {order.metadata?.invoice?.status === "ISSUED" && (
                                <>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    className="flex items-center gap-1"
                                    onClick={() =>
                                      setSelectedInvoice({
                                        series: order.invoiceSeries!,
                                        number: order.invoiceNumber!,
                                        orderId: order.id,
                                        action: "cancel",
                                      })
                                    }
                                  >
                                    <Ban className="w-4 h-4" />
                                    Anulează
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    className="flex items-center gap-1"
                                    onClick={() =>
                                      setSelectedInvoice({
                                        series: order.invoiceSeries!,
                                        number: order.invoiceNumber!,
                                        orderId: order.id,
                                        action: "storno",
                                      })
                                    }
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                    Storno
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-1"
                                    onClick={() =>
                                      setSelectedInvoice({
                                        series: order.invoiceSeries!,
                                        number: order.invoiceNumber!,
                                        orderId: order.id,
                                        action: "email",
                                      })
                                    }
                                  >
                                    <Mail className="w-4 h-4" />
                                    Trimite Email
                                  </Button>
                                </>
                              )}
                              {/* Show restore button for canceled invoices */}
                              {order.metadata?.invoice?.status ===
                                "CANCELED" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex items-center gap-1"
                                  onClick={async () => {
                                    try {
                                      await fetchWithAuth(
                                        `/api/admin/invoices/${order.invoiceSeries}/${order.invoiceNumber}/restore`,
                                        {
                                          method: "POST",
                                        }
                                      );
                                      queryClient.invalidateQueries({
                                        queryKey: ["invoice-orders"],
                                      });
                                      toast.success(
                                        "Factură restaurată cu succes"
                                      );
                                    } catch (error) {
                                      console.error(
                                        "Failed to restore invoice:",
                                        error
                                      );
                                      toast.error(
                                        "Eroare la restaurarea facturii"
                                      );
                                    }
                                  }}
                                >
                                  <RotateCcw className="w-4 h-4" />
                                  Restaurează
                                </Button>
                              )}
                            </>
                          }
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Invoice Status with last checked time */}
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              {isFetching && (
                                <span className="text-xs text-gray-400">
                                  Se actualizează...
                                </span>
                              )}
                              {order.metadata?.invoice?.status === "ISSUED" && (
                                <Badge className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 ring-1 ring-green-200">
                                  <CheckCircle2 className="w-3 h-3 inline-block mr-1" />
                                  Emisă
                                </Badge>
                              )}
                              {order.metadata?.invoice?.status ===
                                "CANCELED" && (
                                <Badge className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800 ring-1 ring-red-200">
                                  <Ban className="w-3 h-3 inline-block mr-1" />
                                  Anulată
                                  {order.metadata?.invoice?.cancelReason && (
                                    <span className="ml-1 opacity-75">
                                      ({order.metadata.invoice.cancelReason})
                                    </span>
                                  )}
                                </Badge>
                              )}
                              {order.metadata?.invoice?.status === "STORNO" && (
                                <Badge className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800 ring-1 ring-purple-200">
                                  <RefreshCw className="w-3 h-3 inline-block mr-1" />
                                  Stornată
                                </Badge>
                              )}
                              {!isFetching &&
                                order.metadata?.invoice?.lastChecked && (
                                  <span className="text-xs text-gray-400">
                                    Ultima verificare:{" "}
                                    {new Date(
                                      order.metadata.invoice.lastChecked
                                    ).toLocaleTimeString()}
                                  </span>
                                )}
                            </div>
                          </div>

                          {/* Payment Status - only show for active invoices */}
                          {order.metadata?.invoice?.status === "ISSUED" && (
                            <>
                              {order.metadata?.invoice?.paymentStatus ===
                                "PAID" && (
                                <Badge className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 ring-1 ring-green-200">
                                  <CheckCircle2 className="w-3 h-3 inline-block mr-1" />
                                  Plătită
                                </Badge>
                              )}
                              {order.metadata?.invoice?.paymentStatus ===
                                "UNPAID" && (
                                <Badge className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200">
                                  <AlertTriangle className="w-3 h-3 inline-block mr-1" />
                                  Neplătită
                                </Badge>
                              )}
                            </>
                          )}

                          {/* Issue Date */}
                          {order.metadata?.invoice?.issuedAt && (
                            <span className="text-xs text-muted-foreground">
                              {new Date(
                                order.metadata.invoice.issuedAt
                              ).toLocaleDateString()}
                            </span>
                          )}

                          {/* Payment Details */}
                          {order.metadata?.invoice?.paidAmount &&
                            order.metadata.invoice.paidAmount > 0 && (
                              <span className="text-xs text-muted-foreground">
                                Plătit:{" "}
                                {formatPrice(
                                  Number(order.metadata.invoice.paidAmount)
                                )}
                              </span>
                            )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Pagination */}
      {ordersResponse?.pagination &&
        ordersResponse.pagination.total > pageSize && (
          <div className="flex justify-center mt-6">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4"
              >
                Anterior
              </Button>
              <span className="text-sm text-muted-foreground">
                Pagina {page} din {ordersResponse?.pagination?.pages}
              </span>
              <Button
                variant="outline"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= (ordersResponse?.pagination?.pages || 1)}
                className="px-4"
              >
                Următor
              </Button>
            </div>
          </div>
        )}

      {/* Invoice Action Dialog */}
      <Dialog
        open={!!selectedInvoice && selectedInvoice.action !== "email"}
        onOpenChange={() => setSelectedInvoice(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedInvoice?.action === "cancel" && "Anulare Factură"}
              {selectedInvoice?.action === "storno" && "Stornare Factură"}
              {selectedInvoice?.action === "email" &&
                "Trimite Factură pe Email"}
            </DialogTitle>
            <DialogDescription>
              {selectedInvoice?.action === "cancel" && (
                <>
                  Sunteți sigur că doriți să anulați factura{" "}
                  {selectedInvoice?.series}/{selectedInvoice?.number}? Această
                  acțiune nu poate fi anulată.
                </>
              )}
              {selectedInvoice?.action === "storno" && (
                <>
                  Sunteți sigur că doriți să stornați factura{" "}
                  {selectedInvoice?.series}/{selectedInvoice?.number}? Se va
                  genera o factură de storno.
                </>
              )}
              {selectedInvoice?.action === "email" && (
                <>
                  Introduceți adresa de email pentru factura{" "}
                  {selectedInvoice?.series}/{selectedInvoice?.number}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (selectedInvoice) {
                const formData = new FormData(e.currentTarget);
                invoiceMutation.mutate({
                  series: selectedInvoice.series,
                  number: selectedInvoice.number,
                  action: selectedInvoice.action,
                  reason:
                    selectedInvoice.action === "cancel"
                      ? (formData.get("reason") as string) || "Anulare factură"
                      : undefined,
                });
              }
            }}
          >
            {selectedInvoice?.action === "cancel" && (
              <div className="py-4">
                <Input name="reason" placeholder="Motivul anulării" required />
              </div>
            )}
            {selectedInvoice?.action === "email" && (
              <div className="py-4 space-y-4">
                <Input
                  name="email"
                  type="email"
                  placeholder="Adresa de email"
                  required
                />
                <Input name="subject" placeholder="Subiect email (opțional)" />
                <textarea
                  name="body"
                  className="w-full h-24 p-2 border rounded"
                  placeholder="Mesaj email (opțional)"
                />
              </div>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setSelectedInvoice(null)}
              >
                Renunță
              </Button>
              <Button
                type="submit"
                variant="default"
                disabled={invoiceMutation.isPending}
              >
                {invoiceMutation.isPending
                  ? selectedInvoice?.action === "cancel"
                    ? "Se anulează..."
                    : selectedInvoice?.action === "storno"
                    ? "Se stornează..."
                    : selectedInvoice?.action === "email"
                    ? "Se trimite..."
                    : "Se procesează..."
                  : selectedInvoice?.action === "cancel"
                  ? "Confirmă Anularea"
                  : selectedInvoice?.action === "storno"
                  ? "Confirmă Stornarea"
                  : "Trimite Email"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
